import { ImportedListing } from '../_types/imported-listing'
import { ListingTag } from '../_types/listing-tag'
import { QuickPassProfile } from '../_types/quickpass-profile'

const DAYS_IN_WEEK = 7
const MS_IN_DAY = 24 * 60 * 60 * 1000
const PRICE_MOVE_MIN_FRACTION = 0.005

interface WeeklyTagCounts {
  yes: number
  maybe: number
  no: number
}

interface PriceMove {
  listingId: string
  listingTitle: string
  fromPrice: number
  toPrice: number
  changedBy: number
  changedFraction: number
}

interface WeeklyDigestSummary {
  daysCovered: number
  windowStartIso: string
  windowEndIso: string
  newListingsAdded: number
  totalListingsTracked: number
  listingsTagged: WeeklyTagCounts
  priceDropCount: number
  priceRiseCount: number
  biggestPriceDrop: PriceMove | null
  biggestPriceRise: PriceMove | null
  newAchievementsUnlocked: string[]
  notesAddedCount: number
  hasMeaningfulActivity: boolean
}

const computeWeeklyPriceMoves = (
  listing: ImportedListing,
  windowStartMs: number,
): { drop: PriceMove | null; rise: PriceMove | null } => {
  const inWindow = listing.priceHistory.filter(
    entry => entry.observedAt >= windowStartMs && entry.priceMonthly !== null,
  )

  if (inWindow.length < 2) {
    return { drop: null, rise: null }
  }

  const oldest = inWindow[0]
  const newest = inWindow[inWindow.length - 1]

  if (oldest.priceMonthly === null || newest.priceMonthly === null) {
    return { drop: null, rise: null }
  }

  const fromPrice = oldest.priceMonthly
  const toPrice = newest.priceMonthly
  const changedBy = toPrice - fromPrice

  if (fromPrice === 0) {
    return { drop: null, rise: null }
  }

  const changedFraction = Math.abs(changedBy) / fromPrice

  if (changedFraction < PRICE_MOVE_MIN_FRACTION) {
    return { drop: null, rise: null }
  }

  const move: PriceMove = {
    listingId: listing.id,
    listingTitle: listing.title,
    fromPrice,
    toPrice,
    changedBy: Math.abs(changedBy),
    changedFraction,
  }

  if (changedBy < 0) {
    return { drop: move, rise: null }
  }

  return { drop: null, rise: move }
}

const pickBigger = (
  current: PriceMove | null,
  candidate: PriceMove | null,
): PriceMove | null => {
  if (current === null) {
    return candidate
  }

  if (candidate === null) {
    return current
  }

  return candidate.changedBy > current.changedBy ? candidate : current
}

/**
 * Compresses the renter's last 7 days of activity into a JSON-friendly
 * summary. The LLM weekly-digest endpoint reads this as its only context.
 *
 * @param {QuickPassProfile} profile - the renter profile, kept on the
 *   signature so callers always pass through the latest gate even when
 *   the helper doesn't currently fold it into the summary
 * @param {ImportedListing[]} listings - imported listings (any age — the
 *   helper filters internally)
 * @param {Record<string, ListingTag>} tags - the renter's tag map
 * @param {Record<string, string>} notes - the renter's listing-note map
 * @param {string[]} unlockedAchievementIds - achievements the renter
 *   currently has
 * @param {number} now - timestamp marking "now" (for testability)
 * @returns {WeeklyDigestSummary} the rolled-up activity window
 */
const computeWeeklyDigestSummary = (
  profile: QuickPassProfile,
  listings: ImportedListing[],
  tags: Record<string, ListingTag>,
  notes: Record<string, string>,
  unlockedAchievementIds: string[],
  now: number,
): WeeklyDigestSummary => {
  void profile

  const windowStartMs = now - DAYS_IN_WEEK * MS_IN_DAY

  const newListings = listings.filter(
    listing => listing.createdAt >= windowStartMs,
  )

  const tagCounts: WeeklyTagCounts = { yes: 0, maybe: 0, no: 0 }
  for (const listing of listings) {
    const tag = tags[listing.id]
    if (tag === 'yes' || tag === 'maybe' || tag === 'no') {
      tagCounts[tag] = tagCounts[tag] + 1
    }
  }

  let priceDropCount = 0
  let priceRiseCount = 0
  let biggestDrop: PriceMove | null = null
  let biggestRise: PriceMove | null = null

  for (const listing of listings) {
    const moves = computeWeeklyPriceMoves(listing, windowStartMs)
    if (moves.drop !== null) {
      priceDropCount = priceDropCount + 1
      biggestDrop = pickBigger(biggestDrop, moves.drop)
    }
    if (moves.rise !== null) {
      priceRiseCount = priceRiseCount + 1
      biggestRise = pickBigger(biggestRise, moves.rise)
    }
  }

  const notesAddedCount = Object.values(notes).filter(
    value => typeof value === 'string' && value.trim().length > 0,
  ).length

  const recentAchievements = unlockedAchievementIds.slice(-5)

  const totalActivitySignals =
    newListings.length +
    tagCounts.yes +
    tagCounts.maybe +
    tagCounts.no +
    priceDropCount +
    priceRiseCount +
    recentAchievements.length

  const summary: WeeklyDigestSummary = {
    daysCovered: DAYS_IN_WEEK,
    windowStartIso: new Date(windowStartMs).toISOString(),
    windowEndIso: new Date(now).toISOString(),
    newListingsAdded: newListings.length,
    totalListingsTracked: listings.length,
    listingsTagged: tagCounts,
    priceDropCount,
    priceRiseCount,
    biggestPriceDrop: biggestDrop,
    biggestPriceRise: biggestRise,
    newAchievementsUnlocked: recentAchievements,
    notesAddedCount,
    hasMeaningfulActivity: totalActivitySignals > 0,
  }

  return summary
}

export {
  type WeeklyDigestSummary,
  type PriceMove,
  type WeeklyTagCounts,
  computeWeeklyDigestSummary,
}
